import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';

import { SortingConfig } from '@customer-portal/shared/models';

import {
  ClearAllUnsavedFilters,
  ClearUnsavedFilters,
  ClearUnsavedPaginationStartIndex,
  SetUnsavedFilters,
  SetUnsavedPaginationStartIndex,
  SetUnsavedSorting,
} from './actions/unsaved-filters.actions';

export interface UnsavedGridState {
  filters?: any;
  sorting?: SortingConfig;
  pagination?: {
    startIndex: number;
  };
}

export type UnsavedFiltersStateModel = Record<string, UnsavedGridState>;

const defaultState: UnsavedFiltersStateModel = {};

@State<UnsavedFiltersStateModel>({
  name: 'unsavedFilters',
  defaults: defaultState,
})
@Injectable()
export class UnsavedFiltersState {
  @Selector()
  static getFiltersByPage(state: UnsavedFiltersStateModel) {
    return (pageKey: string) => state[pageKey]?.filters || null;
  }

  @Selector()
  static getPaginationStartIndex(state: UnsavedFiltersStateModel) {
    return (pageKey: string) => state[pageKey]?.pagination?.startIndex ?? null;
  }

  @Selector()
  static getGridStateByPage(state: UnsavedFiltersStateModel) {
    return (pageKey: string) => state[pageKey] || null;
  }

  @Action(SetUnsavedFilters)
  setUnsavedFilters(
    ctx: StateContext<UnsavedFiltersStateModel>,
    action: SetUnsavedFilters,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      [action.pageKey]: {
        ...state[action.pageKey],
        filters: action.filters,
      },
    });
  }

  @Action(SetUnsavedPaginationStartIndex)
  setUnsavedPaginationStartIndex(
    ctx: StateContext<UnsavedFiltersStateModel>,
    action: SetUnsavedPaginationStartIndex,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      [action.pageKey]: {
        ...state[action.pageKey],
        pagination: {
          ...state[action.pageKey]?.pagination,
          startIndex: action.startIndex,
        },
      },
    });
  }

  @Action(ClearUnsavedFilters)
  clearUnsavedFilters(
    ctx: StateContext<UnsavedFiltersStateModel>,
    action: ClearUnsavedFilters,
  ) {
    const state = ctx.getState();
    const { filters, ...pageState } = state[action.pageKey] || {};

    if (Object.keys(pageState).length > 0) {
      ctx.patchState({ [action.pageKey]: pageState });
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [action.pageKey]: removed, ...remainingState } = state;
      ctx.setState(remainingState);
    }
  }

  @Action(SetUnsavedSorting)
  setUnsavedSorting(
    ctx: StateContext<UnsavedFiltersStateModel>,
    action: SetUnsavedSorting,
  ) {
    const state = ctx.getState();
    ctx.patchState({
      [action.pageKey]: {
        ...state[action.pageKey],
        sorting: action.sorting,
      },
    });
  }

  @Action(ClearUnsavedPaginationStartIndex)
  clearUnsavedPaginationStartIndex(
    ctx: StateContext<UnsavedFiltersStateModel>,
    action: ClearUnsavedPaginationStartIndex,
  ) {
    const state = ctx.getState();
    const pageState = state[action.pageKey];
    if (!pageState?.pagination) return;

    const { pagination, ...remainingPageState } = pageState;

    if (Object.keys(remainingPageState).length > 0) {
      ctx.patchState({ [action.pageKey]: remainingPageState });
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [action.pageKey]: removed, ...remainingState } = state;
      ctx.setState(remainingState);
    }
  }

  @Action(ClearAllUnsavedFilters)
  clearAllUnsavedFilters(ctx: StateContext<UnsavedFiltersStateModel>) {
    ctx.setState(defaultState);
  }
}
